// REMINDERS — "the door is still open: remind HER, every hour, until it is shut."
//
// One rule = one sensor × one state × ONE PERSON. Asked for 2026-10-21 (the
// owner), of the door sensor in Sanjay's room: "if door is open, remind her
// every one hour to close door, and she can use snooze for 5 hours — and all
// this should be settable", from the phone as well as the desktop.
//
// Pure: no I/O, so the wording and the rules are testable. The server runs the
// reminding (Home Automation `api/new/reminders.js` + `reminders_migration.sql`);
// `ui/shared/RfRemindersDialog` is the one screen both shells draw.
//
// ⚠️ ONLY A SENSOR WHOSE STATE CAN END. A reminder stops when the sensor
// reports the opposite event, so it needs a sensor that sends one: a door, a
// safe or a shutter. A `window` sensor here OPENS ONLY and is silent when it
// shuts (rfTypes `openOnly`); motion / fire / gas / panic are events, not
// states. On those a reminder could only ever stop on its repeat cap, so it is
// not offered at all.
// ⚠️ MIRRORED IN THE BACKEND (`reminders.js` REMINDABLE) — change both or neither.
//
// ⚠️ THIS IS NOT THE ROOM'S "door left open" ALERT. `Rooms.DoorOpenAlertMin`
// (SmartRoomDialog) sends ONE notification, at night, in a smart room, to the
// alert grid's audience. A room can have both; `alsoRoomAlert` is the line the
// dialog shows so nobody wonders why two things arrived.

import { rfType, isCloseEvent } from '../devices/rfTypes';

export const REMINDABLE = ['door', 'safe', 'shutter'];

// ⚠️ THE REMINDER SPEAKS (owner, 2026-10-21) — "Please close room door", and
// "Room door closed" when it finally is. Both are res/raw files in the APK
// (scripts/gen-voice-sounds.js, channels in core/push/pushChannels.js) and the
// backend names the same two (api/new/reminders.js VOICE_REMIND / VOICE_CLOSED).
// ⚠️ A PHONE ON AN OLDER BUILD HAS NO channel_voice_* and falls back to the
// system sound, SILENTLY — these need a Play release, never OTA.
export const VOICE_REMIND = 'voice_close_door';
export const VOICE_CLOSED = 'voice_door_closed';

/** May this sensor carry reminders at all? */
export const canRemind = (device) => REMINDABLE.includes(String(device?.RfType || '').toLowerCase());

/** Why not — the sentence the dialog shows instead of an empty editor. */
export function cannotRemindText(device) {
  const t = rfType(device?.RfType);
  if (canRemind(device)) return '';
  if (t.openOnly) {
    return `A ${t.label.toLowerCase()} never reports shutting, so nothing could ever stop the reminders.`;
  }
  return `${t.icon} ${t.label} reports an event, not a state that lasts — there is nothing to be reminded about.`;
}

/**
 * The states of this sensor worth reminding about — its own events, less the
 * closing half of the pair ("Close" ends a reminder, it cannot start one).
 */
export function remindableStates(device) {
  const own = Array.isArray(device?.Events) && device.Events.length
    ? device.Events
    : rfType(device?.RfType).events;
  return [...new Set((own || []).map((e) => String(e).trim()).filter((e) => e && !isCloseEvent(e)))];
}

// What the pickers offer. Minutes throughout — the dialog shows hours where it
// reads better, the server stores minutes only.
export const AFTER_CHOICES = [0, 2, 5, 10, 15, 30, 60];
export const EVERY_CHOICES = [5, 10, 15, 30, 60, 120, 180, 360, 720];
export const SNOOZE_CHOICES = [15, 30, 60, 120, 300, 480, 720];
export const REPEAT_CHOICES = [1, 3, 6, 12, 24, 48];

export const DEFAULT_RULE = {
  Meaning: 'Open',
  Enabled: 1,
  AfterMin: 10,
  EveryMin: 60,
  SnoozeMin: 300,
  MaxRepeats: 24,
  TimeFrom: '',
  TimeTo: '',
  Days: 0,
  ModeIs: '',
  Sound: '',
  ConfirmClose: 1,
  ClosedSound: '',
};

/** "1 h", "5 h", "45 min", "2 h 30 min" — minutes, said the way a person would. */
export function minutesText(mins) {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  if (!m) return 'at once';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} h ${rest} min` : `${h} h`;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The Days bitmask → "Mon, Tue" (empty = every day). Bit 0 = Sunday. */
export function daysText(mask) {
  const m = Number(mask) || 0;
  if (!m) return '';
  return DAY_NAMES.filter((_, i) => m & (1 << i)).join(', ');
}

/**
 * One rule as a sentence — the list row, and the only place the wording lives.
 * `withWho` names the person (the location list), otherwise it is "you".
 */
export function ruleText(rule, { withWho = true } = {}) {
  if (!rule) return '';
  const who = withWho ? (String(rule.UserName || '').trim() || `User ${rule.UserId}`) : 'you';
  const state = String(rule.Meaning || 'Open').toLowerCase();
  const after = Number(rule.AfterMin) > 0 ? ` for ${minutesText(rule.AfterMin)}` : '';
  const bits = [`If it stays ${state}${after}, remind ${who} every ${minutesText(rule.EveryMin)}`];
  const only = [];
  const from = String(rule.TimeFrom || '').slice(0, 5);
  const to = String(rule.TimeTo || '').slice(0, 5);
  if (from && to && from !== to) only.push(`${from}–${to}`);
  const days = daysText(rule.Days);
  if (days) only.push(days);
  if (rule.ModeIs) only.push(String(rule.ModeIs) === 'locked' ? 'only when 🔒 locked' : 'only when 🔓 unlocked');
  if (only.length) bits.push(`only ${only.join(' · ')}`);
  bits.push(`at most ${Number(rule.MaxRepeats) || 1}× per opening`);
  bits.push(`snooze ${minutesText(rule.SnoozeMin)}`);
  if (Number(rule.ConfirmClose) === 1) bits.push('and say so when it is shut');
  return `${bits.join(' · ')}.`;
}

/**
 * What a rule is doing right now, for the row's chip and the snooze bar.
 * `now` is passed in so this stays pure.
 *   off        the rule is disabled
 *   waiting    the sensor is not in that state (nothing to remind about)
 *   counting   in that state, the first reminder is not due yet
 *   snoozed    quiet until SnoozeUntil
 *   stopped    this opening hit the repeat cap, or the state is impossibly old
 *   reminding  sending, every EveryMin
 * `serverTime` parses the backend's datetimes (they arrive IST-labelled-UTC).
 */
export function ruleStatus(rule, { now = Date.now(), serverTime } = {}) {
  if (!rule) return { key: 'off', text: '' };
  const at = (raw) => {
    if (!raw) return null;
    const t = serverTime ? serverTime(raw) : Date.parse(raw);
    return Number.isFinite(t) ? t : null;
  };
  if (!Number(rule.Enabled)) return { key: 'off', text: 'Off' };
  const inState = Number(rule.InState) === 1;
  const stateSec = Math.max(0, Number(rule.StateSec) || 0);
  if (!inState) return { key: 'waiting', text: `Waiting — not ${String(rule.Meaning || '').toLowerCase()}` };
  const stopped = String(rule.Stopped || '');
  if (stopped === 'cap') return { key: 'stopped', text: `Stopped — ${Number(rule.Sent) || 0} reminders sent` };
  // ⚠️ A LOST CLOSE FRAME READS AS "STILL OPEN" (docs/17). The server gives up
  // after REMINDER_MAX_AGE_H and says so rather than nagging for a week.
  if (stopped === 'stale') return { key: 'stopped', text: 'Stopped — open too long to believe' };
  const snoozeUntil = at(rule.SnoozeUntil);
  if (snoozeUntil && snoozeUntil > now) {
    return { key: 'snoozed', text: `Snoozed ${minutesText(Math.round((snoozeUntil - now) / 60000))}`, until: snoozeUntil };
  }
  const afterSec = (Number(rule.AfterMin) || 0) * 60;
  if (stateSec < afterSec) {
    return { key: 'counting', text: `${minutesText(Math.round((afterSec - stateSec) / 60))} to the first reminder` };
  }
  const sent = Number(rule.Sent) || 0;
  return { key: 'reminding', text: sent ? `Reminding — ${sent} sent` : 'Reminding', sent };
}

/** A rule the editor is about to save → the body SaveRfReminder wants. */
export function toSaveBody(rule, { rfDeviceId, userId }) {
  const num = (v, d) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : d;
  };
  return {
    ReminderId: Number(rule.ReminderId) || 0,
    RfDeviceId: Number(rfDeviceId) || Number(rule.RfDeviceId) || 0,
    Meaning: String(rule.Meaning || 'Open').trim() || 'Open',
    UserId: Number(rule.UserId) || Number(userId) || 0,
    Enabled: Number(rule.Enabled) === 0 ? 0 : 1,
    AfterMin: num(rule.AfterMin, DEFAULT_RULE.AfterMin),
    EveryMin: Math.max(1, num(rule.EveryMin, DEFAULT_RULE.EveryMin)),
    SnoozeMin: num(rule.SnoozeMin, DEFAULT_RULE.SnoozeMin),
    MaxRepeats: Math.max(1, num(rule.MaxRepeats, DEFAULT_RULE.MaxRepeats)),
    TimeFrom: String(rule.TimeFrom || '').slice(0, 5),
    TimeTo: String(rule.TimeTo || '').slice(0, 5),
    Days: num(rule.Days, 0),
    ModeIs: ['locked', 'unlocked'].includes(String(rule.ModeIs)) ? String(rule.ModeIs) : '',
    Sound: String(rule.Sound || ''),
    ConfirmClose: Number(rule.ConfirmClose) === 0 ? 0 : 1,
    ClosedSound: String(rule.ClosedSound || ''),
  };
}

/** What stops the editor from saving — '' when it is fine. */
export function ruleProblem(rule) {
  if (!rule) return 'nothing to save';
  if (!Number(rule.UserId)) return 'Choose who should be reminded.';
  if (!String(rule.Meaning || '').trim()) return 'Choose the state to remind about.';
  if (isCloseEvent(rule.Meaning)) return `“${rule.Meaning}” is the end of a state, not a state to remind about.`;
  if (!(Number(rule.EveryMin) > 0)) return 'How often should the reminder repeat?';
  const from = String(rule.TimeFrom || '');
  const to = String(rule.TimeTo || '');
  if ((from && !to) || (to && !from)) return 'A time window needs both a start and an end.';
  if (from && to && from === to) return 'A time window of zero length would never fire.';
  return '';
}

/** The line warning that the ROOM also alerts once at night, so two arrive. */
export function alsoRoomAlert(room) {
  const mins = Number(room?.DoorOpenAlertMin) || 0;
  if (!mins) return '';
  return `This room also sends one “door left open” alert at night after ${minutesText(mins)}`
    + ' (Smart room settings) — that one is separate from these reminders.';
}
